import React, { useCallback, useEffect } from 'react';
import { BackHandler } from 'react-native';

import {
  createCheckIn,
  createReview,
  finalizeCheckIn,
  uploadCheckInPhoto,
} from '@/api/endpoints/check-ins';
import { sendFriendRecommendation } from '@/api/endpoints/friend-recommendation';
import { createNote, uploadNotePhoto } from '@/api/endpoints/notes';
import { NOTES_QUERY_KEYS } from '@/api/queries/notes';
import { toast } from '@/components/ui/toast';
import { FEED_QUERY_KEYS } from '@/features/activity/hooks/use-activity';
import { confirmDialog } from '@/utils/dialog';
import { getExtensionForMimeType } from '@/utils/mime';
import Sentry from '@/utils/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { NoteEntryState } from '../../notes/types';
import { CheckInEntryState, CheckInSuccessData } from '../types';
import { createEmptyNoteEntry, QuickCheckInState } from './use-check-in-form-state';

export interface UseCheckInActionsOptions {
  state: QuickCheckInState;
  hasUnsavedChanges: boolean;
  canSubmit: boolean;
  canSubmitNotes: boolean;
  readyEntries: CheckInEntryState[];
  readyNoteEntries: NoteEntryState[];
  draftIdRef: React.MutableRefObject<string | null>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSavingDraft: React.Dispatch<React.SetStateAction<boolean>>;
  setLastSavedState: React.Dispatch<React.SetStateAction<QuickCheckInState | null>>;
  setNoteEntries: React.Dispatch<React.SetStateAction<NoteEntryState[]>>;
  coords: { latitude: number; longitude: number } | null;
  saveDraft: (draft: any) => Promise<string>;
  deleteDraft: (id: string) => Promise<void>;
  onSubmitSuccess?: (data: CheckInSuccessData) => void;
}

export function useCheckInActions({
  state,
  hasUnsavedChanges,
  canSubmit,
  canSubmitNotes,
  readyEntries,
  readyNoteEntries,
  draftIdRef,
  setIsSubmitting,
  setIsSavingDraft,
  setLastSavedState,
  setNoteEntries,
  coords,
  saveDraft,
  deleteDraft,
  onSubmitSuccess,
}: UseCheckInActionsOptions) {
  const queryClient = useQueryClient();

  const handleSaveDraft = useCallback(async () => {
    if (!hasUnsavedChanges) return;

    setIsSavingDraft(true);
    try {
      const firstEntry = state.entries[0];
      const draftId = await saveDraft({
        id: draftIdRef.current || undefined,
        checkInId: null,
        business: state.business
          ? { id: state.business.id, name: state.business.name }
          : null,
        menuItem: firstEntry?.menuItem
          ? { id: firstEntry.menuItem.id, name: firstEntry.menuItem.name }
          : null,
        ratingId: firstEntry?.ratingId ?? null,
        diningType: state.diningType,
        photos: firstEntry?.photos.map((p) => p.uri) ?? [],
        reviewContent: firstEntry?.reviewContent ?? '',
        entries: state.entries.map((e) => ({
          menuItem: e.menuItem
            ? { id: e.menuItem.id, name: e.menuItem.name }
            : null,
          ratingId: e.ratingId,
          photos: e.photos.map((p) => p.uri),
          reviewContent: e.reviewContent,
          recommendTo: e.recommendTo,
        })),
      });
      draftIdRef.current = draftId;

      setLastSavedState({ ...state, entries: state.entries.map((e) => ({ ...e })) });

      toast.success('Draft saved');
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft', { description: 'Please try again.' });
    } finally {
      setIsSavingDraft(false);
    }
  }, [hasUnsavedChanges, state, saveDraft, draftIdRef, setIsSavingDraft, setLastSavedState]);

  const handleClose = useCallback(() => {
    const navigateBack = () => {
      if (router.canDismiss()) {
        router.dismiss();
      } else {
        router.replace('/(tabs)');
      }
    };

    if (hasUnsavedChanges) {
      confirmDialog(
        'Discard Changes?',
        'You have unsaved changes. Push OK to discard, or Cancel to stay.',
        navigateBack,
        () => {},
      );
    } else {
      navigateBack();
    }
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleBackPress = () => {
      if (hasUnsavedChanges) {
        handleClose();
        return true;
      }
      return false;
    };

    const subscription = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    return () => subscription.remove();
  }, [hasUnsavedChanges, handleClose]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !state.business) return;

    setIsSubmitting(true);

    let successCount = 0;
    let failCount = 0;

    try {
      for (const entry of readyEntries) {
        try {
          Sentry.addBreadcrumb({
            category: 'check-in',
            message: 'Creating draft check-in',
            level: 'info',
            data: {
              businessId: state.business.id,
              menuItemId: entry.menuItem!.id,
              ratingId: entry.ratingId,
              photoCount: entry.photos.length,
              hasReview: entry.reviewContent.trim().length > 0,
            },
          });

          const checkIn = await createCheckIn({
            business_id: state.business.id,
            menu_item_id: entry.menuItem!.id,
            rating_id: entry.ratingId!,
            latitude: coords?.latitude,
            longitude: coords?.longitude,
            dining_type: state.diningType,
            ...(state.occurredAt ? { occurred_at: state.occurredAt } : {}),
          });

          for (let i = 0; i < entry.photos.length; i++) {
            const photo = entry.photos[i];
            const mimeType = photo.mimeType || 'image/jpeg';
            const ext = getExtensionForMimeType(photo.mimeType);
            await uploadCheckInPhoto(checkIn.id, {
              uri: photo.uri,
              type: mimeType,
              name: `check-in-photo-${Date.now()}${ext}`,
              file: photo.file,
            });
          }

          if (entry.reviewContent.trim()) {
            try {
              await createReview(checkIn.id, {
                content: entry.reviewContent.trim(),
              });
            } catch (reviewErr) {
              console.error('Review creation failed:', reviewErr);
            }
          }

          await finalizeCheckIn(checkIn.id);
          successCount++;

          for (const friend of entry.recommendTo) {
            sendFriendRecommendation({
              recipient_id: friend.id,
              menu_item_id: entry.menuItem!.id,
            }).catch((err) => {
              console.warn('[CheckIn] Recommendation failed:', err);
            });
          }
        } catch (entryErr) {
          failCount++;
          Sentry.captureException(entryErr);
          console.error('Check-in entry submission failed:', entryErr);
        }
      }

      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });

      if (draftIdRef.current) {
        try {
          await deleteDraft(draftIdRef.current);
        } catch (draftErr) {
          console.error('Failed to delete draft:', draftErr);
        }
      }

      if (failCount === 0 && onSubmitSuccess) {
        const firstEntry = readyEntries[0];
        onSubmitSuccess({
          menuItemId: firstEntry.menuItem!.id,
          menuItemName: firstEntry.menuItem!.name,
          businessName: state.business!.name,
          ratingKey: firstEntry.ratingKey || 'try_it',
          coverPhotoUrl: firstEntry.menuItem!.coverPhotoUrl,
          checkInCount: firstEntry.menuItem!.checkInCount,
        });
      } else if (failCount === 0) {
        router.replace('/(tabs)');
        toast.success(
          successCount === 1
            ? 'Check-in posted'
            : `${successCount} check-ins posted`
        );
      } else {
        router.replace('/(tabs)');
        toast.error(`${failCount} failed`, {
          description: `${successCount} check-in${successCount !== 1 ? 's' : ''} posted successfully`,
        });
      }
    } catch (err) {
      console.error('Check-in submission failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit check-in';
      toast.error('Submission failed', { description: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, state, readyEntries, coords, deleteDraft, queryClient, draftIdRef, setIsSubmitting, onSubmitSuccess]);

  const handleSaveNotes = useCallback(async () => {
    if (!canSubmitNotes || !state.business) return;

    setIsSubmitting(true);

    let successCount = 0;
    let failCount = 0;

    try {
      for (const entry of readyNoteEntries) {
        try {
          Sentry.addBreadcrumb({
            category: 'note',
            message: 'Creating note',
            level: 'info',
            data: {
              businessId: state.business.id,
              menuItemId: entry.menuItem!.id,
              hasPhoto: entry.photo !== null,
            },
          });

          const note = await createNote({
            business_id: state.business.id,
            menu_item_id: entry.menuItem!.id,
            content: entry.content.trim(),
          });

          if (entry.photo) {
            const mimeType = entry.photo.mimeType || 'image/jpeg';
            const ext = getExtensionForMimeType(entry.photo.mimeType);
            await uploadNotePhoto(note.id, {
              uri: entry.photo.uri,
              type: mimeType,
              name: `note-photo-${Date.now()}${ext}`,
              file: entry.photo.file,
            });
          }

          successCount++;
        } catch (entryErr) {
          failCount++;
          Sentry.captureException(entryErr);
          console.error('Note entry save failed:', entryErr);
        }
      }

      queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });

      setNoteEntries([createEmptyNoteEntry()]);

      router.replace('/(tabs)');

      if (failCount === 0) {
        toast.success(
          successCount === 1 ? 'Note saved' : `${successCount} notes saved`
        );
      } else {
        toast.error(`${failCount} failed`, {
          description: `${successCount} note${successCount !== 1 ? 's' : ''} saved successfully`,
        });
      }
    } catch (err) {
      console.error('Note save failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save notes';
      toast.error('Save failed', { description: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmitNotes, state.business, readyNoteEntries, queryClient, setIsSubmitting, setNoteEntries]);

  return {
    handleSaveDraft,
    handleClose,
    handleSubmit,
    handleSaveNotes,
  };
}
